import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { HeroCard } from '@/components/ui/HeroCard';
import { VideoFeatures } from '@/store/videoFeatures';
import { Settings } from 'lucide-react';
import { toast } from 'sonner';

interface MasterControlToggleProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const MasterControlToggle: React.FC<MasterControlToggleProps> = ({
  features,
  setFeature,
}) => {
  useLanguage();
  const handleToggle = (value: boolean) => {
    // Se ativando controles
    if (value) {
      // Desativar autoplay se estiver ativo
      if (features.autoplayOverlay?.enabled) {
        setFeature('autoplayOverlay', {
          ...features.autoplayOverlay,
          enabled: false
        });
        setFeature('autoPlay', false);
        toast.info(translateInterfaceText("Autoplay foi desativado automaticamente. Autoplay e controles não podem estar ativos ao mesmo tempo."));
      }

      // Desativar barra fake se estiver ativa.
      // OBS: features.showProgressBar é a barra de progresso INTERNA dos
      // controles (sub-recurso) e PODE coexistir com os controles —
      // não deve ser desativada aqui. Só a barra fake (progressBar.enabled)
      // é incompatível com os controles.
      if (features.progressBar?.enabled) {
        setFeature('progressBar', {
          ...(features.progressBar || {}),
          enabled: false,
        });
        toast.info(translateInterfaceText("Barra fake foi desativada automaticamente. Barra fake e controles não podem estar ativos ao mesmo tempo."));
      }

      // Garante que "Desativar Controles" não esteja ligado: se estivesse,
      // todos os sub-toggles (inclusive o switch "Barra de Progresso") seriam
      // renderizados desabilitados (não-interativos) e dariam a impressão de
      // que foram automaticamente desativados ao ligar o master.
      if (features.disableControls) {
        setFeature('disableControls', false);
        setFeature('noneControlsMoreAutoplay', false);
      }

      // Ativar tema personalizado automaticamente
      setFeature('playerThemeEnabled', true);
    } else {
      // Ao desativar controles, resetar todas as propriedades relacionadas
      setFeature('disableControls', false);
      setFeature('noneControlsMoreAutoplay', false);
    }

    setFeature('showControls', value);
  };

  return (
    <HeroCard
      icon={<Settings className="w-full h-full" strokeWidth={2} />}
      title={translateInterfaceText("Controles do Player")}
      description={translateInterfaceText("Permita interação com o vídeo e aumente controle, retenção e experiência do usuário.")}
      isActive={features.showControls}
      onToggle={handleToggle}
    />
  );
};
